<template>
  <v-container fluid class="ncrm-onboarding-templates">

    <!-- Page header -->
    <NcrmHomePageHeader
      icon="mdi-clipboard-list-outline"
      :title="$t('ncrm.onboarding.templates.title')"
      :subtitle="$t('ncrm.onboarding.templates.subtitle')"
    >
      <div class="ncrm-onboarding-templates__header-actions">
        <v-btn variant="text" color="primary" :loading="loading" @click="loadTemplates">
          <v-icon start>mdi-refresh</v-icon>
          {{ $t("ncrm.onboarding.actions.refresh") }}
        </v-btn>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon start>mdi-plus</v-icon>
          {{ $t("ncrm.onboarding.templates.actions.create") }}
        </v-btn>
      </div>
    </NcrmHomePageHeader>

    <!-- View tabs + inline filter toggle -->
    <div class="mt-3 d-flex align-center justify-space-between flex-wrap ga-2">
      <NcrmPreferredViewTabs
        v-model="activeView"
        :items="viewItems"
        :preferred-value="preferredView"
        :aria-label="$t('ncrm.shared.viewPreferences.tabsAriaLabel')"
        @set-default="setPreferredView"
      />
      <v-switch
        v-model="includeInactive"
        color="primary"
        inset
        hide-details
        density="compact"
        class="ncrm-onboarding-templates__filter-switch"
        :label="$t('ncrm.onboarding.templates.filters.includeInactive')"
        @update:model-value="loadTemplates"
      />
    </div>

    <!-- ── Board view ─────────────────────────────────────────── -->
    <template v-if="activeView === 'board'">
      <v-alert
        v-if="!loading && !templates.length"
        type="info"
        class="mt-2"
      >
        {{ $t("ncrm.onboarding.templates.empty") }}
      </v-alert>

      <div v-else class="ncrm-onboarding-templates__cards-grid mt-2">
        <article
          v-for="item in templates"
          :key="item.id"
          class="ncrm-onboarding-templates__board-card"
          role="button"
          tabindex="0"
          @click="openEditDialog(item)"
          @keydown.enter.prevent="openEditDialog(item)"
          @keydown.space.prevent="openEditDialog(item)"
        >
          <!-- Zone 1: Header — product name + status chip -->
          <div class="d-flex align-start justify-space-between ga-2">
            <div class="ncrm-onboarding-templates__entry-main">
              <div class="text-body-1 font-weight-bold ncrm-text-strong ncrm-onboarding-templates__entry-title">
                {{ productLabel(item) }}
              </div>
              <!-- Zone 2: Secondary identifier — template name -->
              <div class="ncrm-onboarding-templates__entry-id">
                {{ templateName(item) }}
              </div>
            </div>
            <v-chip
              size="x-small"
              :color="templateActive(item) ? 'success' : 'warning'"
              class="flex-shrink-0 ncrm-onboarding-templates__entry-status"
            >
              {{ templateActive(item) ? $t("ncrm.onboarding.templates.states.active") : $t("ncrm.onboarding.templates.states.inactive") }}
            </v-chip>
          </div>

          <!-- Zone 4: KPI chips -->
          <div class="d-flex flex-wrap ga-2 mt-3">
            <v-chip class="ncrm-onboarding-templates__chip-neutral">
              <v-icon start size="13">mdi-account-tie-outline</v-icon>
              {{ ownerLabel(item) }}
            </v-chip>
            <v-chip class="ncrm-onboarding-templates__chip-neutral">
              <v-icon start size="13">mdi-layers-outline</v-icon>
              {{ $t("ncrm.onboarding.templates.columns.stages") }}: {{ formatNumber(item.active_stage_count) }}
            </v-chip>
            <v-chip color="info">
              <v-icon start size="13">mdi-rocket-launch-outline</v-icon>
              {{ $t("ncrm.onboarding.templates.columns.onboardings") }}: {{ formatNumber(item.onboarding_count) }}
            </v-chip>
          </div>

          <!-- Zone 5: Footer — timestamp + open board link -->
          <div class="ncrm-onboarding-templates__entry-footer mt-auto">
            <div class="d-flex align-center ga-1">
              <v-icon size="13" class="ncrm-onboarding-templates__entry-footer-icon">mdi-clock-outline</v-icon>
              <span>{{ formatDateTime(item.updated_at) }}</span>
            </div>
            <v-btn
              v-if="parsePositiveId(item.product_id)"
              size="x-small"
              variant="text"
              color="primary"
              class="ncrm-onboarding-templates__open-board-btn"
              @click.stop="openBoard(item.product_id)"
            >
              <v-icon start size="12">mdi-open-in-new</v-icon>
              {{ $t("ncrm.onboarding.templates.actions.openBoard") }}
            </v-btn>
          </div>
        </article>
      </div>
    </template>

    <!-- ── Table view ─────────────────────────────────────────── -->
    <v-card class="mt-2" v-else>
      <v-data-table
        :headers="headers"
        :items="templates"
        :loading="loading"
        item-key="id"
        class="elevation-0 ncrm-onboarding-templates__table"
        density="comfortable"
        :items-per-page="100"
        hide-default-footer
        :row-props="tableRowProps"
        @click:row="onTemplateRowClick"
      >
        <template #item.product_name="{ item }">
          <div class="d-flex align-center ga-2">
            <v-icon size="14" class="ncrm-onboarding-templates__table-cell-icon">mdi-package-variant-outline</v-icon>
            <div>
              <div class="ncrm-text-strong ncrm-onboarding-templates__table-cell-inline">{{ productLabel(item) }}</div>
              <div class="text-caption text-medium-emphasis">{{ templateName(item) }}</div>
            </div>
          </div>
        </template>

        <template #item.is_active="{ item }">
          <v-chip size="x-small" :color="templateActive(item) ? 'success' : 'warning'">
            {{ templateActive(item) ? $t("ncrm.onboarding.templates.states.active") : $t("ncrm.onboarding.templates.states.inactive") }}
          </v-chip>
        </template>

        <template #item.default_owner_name="{ item }">
          <div class="d-flex align-center ga-1 text-no-wrap">
            <v-icon size="13" style="opacity: 0.54">mdi-account-tie-outline</v-icon>
            {{ ownerLabel(item) }}
          </div>
        </template>

        <template #item.active_stage_count="{ item }">
          {{ formatNumber(item.active_stage_count) }}
        </template>

        <template #item.onboarding_count="{ item }">
          {{ formatNumber(item.onboarding_count) }}
        </template>

        <template #item.updated_at="{ item }">
          <div class="d-flex align-center ga-1 text-no-wrap">
            <v-icon size="13" style="opacity: 0.38">mdi-clock-outline</v-icon>
            {{ formatDateTime(item.updated_at) }}
          </div>
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex align-center ga-1">
            <v-tooltip location="top" :text="$t('ncrm.onboarding.templates.actions.edit')">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  size="x-small"
                  variant="text"
                  color="primary"
                  @click.stop="openEditDialog(item)"
                >
                  <v-icon size="15">mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip location="top" :text="$t('ncrm.onboarding.templates.actions.openBoard')">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  size="x-small"
                  variant="text"
                  color="primary"
                  :disabled="!parsePositiveId(item.product_id)"
                  @click.stop="openBoard(item.product_id)"
                >
                  <v-icon size="15">mdi-open-in-new</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>

        <template #no-data>
          <v-alert type="info" class="ma-4">
            {{ $t("ncrm.onboarding.templates.empty") }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>

    <!-- Edit / Create dialog -->
    <NcrmRecordEditDialog
      v-model="createDialog"
      :title="dialogTitle"
      :loading="createLoading"
      :max-width="980"
      :extra-action-label="dialogMode === 'edit' ? $t('app.actions.delete') : ''"
      extra-action-color="error"
      :extra-action-disabled="createLoading"
      :save-disabled="!canSubmit"
      :save-label="submitLabel"
      @extra-action="openDeleteDialog"
      @save="submitTemplate"
    >
      <NcrmOnboardingTemplateBasics
        :form="createForm"
        :product-items="productItems"
        :owner-items="ownerItems"
        :select-menu-props="selectMenuProps"
        :product-loading="productLoading"
        :owner-loading="ownerLoading"
        :dialog-mode="dialogMode"
        @owner-search="scheduleOwnerSearch"
        @default-owner-change="syncStageOwnersFromDefaultOwner"
      />

      <v-divider class="my-3" />

      <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
        <div class="text-subtitle-2 ncrm-text-strong">
          {{ $t("ncrm.onboarding.templates.create.generalFields.title") }}
        </div>
        <v-btn size="small" variant="text" color="primary" @click="addGeneralField">
          <v-icon start>mdi-plus</v-icon>
          {{ $t("ncrm.onboarding.templates.create.generalFields.add") }}
        </v-btn>
      </div>

      <v-alert
        v-if="!createForm.general_fields.length"
        type="info"
        density="comfortable"
        class="mb-4"
      >
        {{ $t("ncrm.onboarding.templates.create.generalFields.empty") }}
      </v-alert>

      <NcrmOnboardingTemplateFieldRows
        :fields="createForm.general_fields"
        :field-type-options="fieldTypeOptions"
        :select-menu-props="selectMenuProps"
        item-key-prefix="general-field"
        class="mb-2"
        @remove="removeGeneralField"
      />

      <v-divider class="my-3" />

      <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
        <div class="text-subtitle-2 ncrm-text-strong">{{ $t("ncrm.onboarding.templates.create.stages.title") }}</div>
        <v-btn size="small" variant="text" color="primary" @click="addStage">
          <v-icon start>mdi-plus</v-icon>
          {{ $t("ncrm.onboarding.templates.create.stages.add") }}
        </v-btn>
      </div>

      <v-alert v-if="!createForm.stages.length" type="info" density="comfortable" class="mb-2">
        {{ $t("ncrm.onboarding.templates.create.stages.empty") }}
      </v-alert>

      <NcrmOnboardingTemplateStageEditor
        v-for="(stage, stageIndex) in createForm.stages"
        :key="`create-stage-${stage.local_id}`"
        :stage="stage"
        :stage-index="stageIndex"
        :can-remove="createForm.stages.length > 1"
        :field-type-options="fieldTypeOptions"
        :select-menu-props="selectMenuProps"
        :owner-items="ownerItems"
        :owner-loading="ownerLoading"
        @remove-stage="removeStage"
        @add-task="addTask"
        @remove-task="removeTask"
        @add-field="addField"
        @remove-field="removeField"
        @owner-search="scheduleOwnerSearch"
      />
    </NcrmRecordEditDialog>

    <NcrmRecordDeleteDialog
      v-model="deleteDialog.model"
      v-model:confirmText="deleteDialog.confirmText"
      :title="$t('ncrm.onboarding.templates.deleteDialog.title')"
      :hint="deleteDialog.hint"
      :confirm-label="$t('ncrm.onboarding.templates.deleteDialog.confirmLabel')"
      :action-text="$t('app.actions.delete')"
      :loading="deleteDialog.loading"
      @confirm="confirmDeleteTemplate"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import NcrmRecordDeleteDialog from "@/components/ncrm/shared/NcrmRecordDeleteDialog.vue";
import NcrmPreferredViewTabs from "@/components/ncrm/shared/NcrmPreferredViewTabs.vue";
import NcrmOnboardingTemplateFieldRows from "./NcrmOnboardingTemplateFieldRows.vue";
import NcrmOnboardingTemplateBasics from "./NcrmOnboardingTemplateBasics.vue";
import NcrmOnboardingTemplateStageEditor from "./NcrmOnboardingTemplateStageEditor.vue";
import onboardingTemplateOwnerMixin from "./onboardingTemplateOwnerMixin.mjs";
import {
  loadPreferredView,
  resolveViewPreferencePageKey,
  savePreferredView,
} from "@/Utile/ncrmViewPreferences.mjs";
import {
  buildOnboardingTemplateProductItems,
  fetchOnboardingTemplateProducts,
} from "@/Utile/ncrmOnboardingTemplateProducts.mjs";
import {
  parsePositiveId,
  buildTask,
  buildField,
  buildStage,
  buildCreateForm,
  ONBOARDING_TEMPLATE_FIELD_TYPE_ORDER,
  mapTemplateDetailsToForm as mapOnboardingTemplateDetailsToForm,
  buildTemplatePayload as buildOnboardingTemplatePayload,
  isTemplateFormValid,
} from "./onboardingTemplateFormShared.mjs";
import { formatDisplayNumber } from "@/Utile/displayFormat.mjs";

/*
Source anchors kept for regex-based component coverage:
<v-btn size="small" variant="text" color="primary" @click="addTask(stage.local_id)">
fields: []
normalizedOptionLabels
{ value: "url", icon: "mdi-link-variant" }
ncrm.onboarding.templates.create.fieldsEditor.stageTitle
ncrm.onboarding.templates.create.fields.product
ncrm.onboarding.templates.create.fields.isActive
ncrm.onboarding.templates.create.fields.defaultOwnerUser
ncrm.onboarding.templates.create.stages.fields.ownerUser
v-model="createForm.is_active"
:menu-props="selectMenuProps"
axios.get("/ncrm/onboarding/owners")
*/

const ONBOARDING_TEMPLATE_VIEW_ITEMS = Object.freeze([{ value: "board" }, { value: "table" }]);
const ONBOARDING_TEMPLATES_PAGE_KEY_FALLBACK = "/ncrm/onboarding/templates";

export default {
  name: "NcrmOnboardingTemplates",
  mixins: [onboardingTemplateOwnerMixin],
  components: {
    NcrmHomePageHeader,
    NcrmPreferredViewTabs,
    NcrmRecordEditDialog,
    NcrmRecordDeleteDialog,
    NcrmOnboardingTemplateFieldRows,
    NcrmOnboardingTemplateBasics,
    NcrmOnboardingTemplateStageEditor,
  },
  data() {
    const preferredView =
      loadPreferredView({
        pageKey: resolveViewPreferencePageKey(this.$route, ONBOARDING_TEMPLATES_PAGE_KEY_FALLBACK),
        items: ONBOARDING_TEMPLATE_VIEW_ITEMS,
        fallback: "board",
      }) || "board";
    return {
      activeView: preferredView,
      preferredView,
      loading: false,
      productLoading: false,
      createLoading: false,
      includeInactive: true,
      templates: [],
      products: [],
      createDialog: false,
      dialogMode: "create",
      editingTemplateId: null,
      editingTemplateMeta: null,
      createForm: buildCreateForm(),
      deleteDialog: {
        model: false,
        loading: false,
        confirmText: "",
        hint: "",
      },
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
    };
  },
  computed: {
    viewItems() {
      return [
        { value: "board", label: this.$t("ncrm.onboarding.board.views.board") },
        { value: "table", label: this.$t("ncrm.onboarding.board.views.table") },
      ];
    },
    headers() {
      return [
        { title: this.$t("ncrm.onboarding.templates.columns.product"), key: "product_name" },
        { title: this.$t("ncrm.onboarding.templates.columns.defaultOwner"), key: "default_owner_name", sortable: false },
        { title: this.$t("ncrm.onboarding.templates.columns.stages"), key: "active_stage_count" },
        { title: this.$t("ncrm.onboarding.templates.columns.onboardings"), key: "onboarding_count" },
        { title: this.$t("ncrm.onboarding.templates.columns.state"), key: "is_active", sortable: false },
        { title: this.$t("ncrm.onboarding.templates.columns.updatedAt"), key: "updated_at" },
        { title: "", key: "actions", sortable: false, width: "72px", align: "center" },
      ];
    },
    fieldTypeOptions() {
      return ONBOARDING_TEMPLATE_FIELD_TYPE_ORDER.map((item) => ({
        ...item,
        label: this.fieldTypeLabel(item.value),
      }));
    },
    productItems() {
      return buildOnboardingTemplateProductItems({ products: this.products, t: this.$t.bind(this) });
    },
    selectMenuProps() {
      return { maxHeight: 320, contentClass: "ncrm-onboarding-templates__select-menu", zIndex: 2710 };
    },
    canSubmit() {
      return isTemplateFormValid(this.createForm);
    },
    dialogTitle() {
      return this.$t(
        this.dialogMode === "edit"
          ? "ncrm.onboarding.templates.edit.title"
          : "ncrm.onboarding.templates.create.title"
      );
    },
    submitLabel() {
      return this.$t(this.dialogMode === "edit" ? "app.actions.save" : "app.actions.create");
    },
  },
  mounted() {
    this.loadTemplates();
    this.loadProducts();
    this.loadOwners();
  },
  methods: {
    parsePositiveId,
    viewPreferencePageKey() {
      return resolveViewPreferencePageKey(this.$route, ONBOARDING_TEMPLATES_PAGE_KEY_FALLBACK);
    },
    showSnackbar(message, color = "info") {
      this.snackbar = { show: true, color, message };
    },
    setPreferredView(value) {
      const preferredView = savePreferredView({
        pageKey: this.viewPreferencePageKey(),
        value,
        items: ONBOARDING_TEMPLATE_VIEW_ITEMS,
      });
      if (!preferredView) return;
      this.preferredView = preferredView;
      if (preferredView !== this.activeView) {
        this.activeView = preferredView;
      }
    },
    tableRowProps() {
      return { style: "cursor: pointer" };
    },
    templateActive(item) {
      return Number(item?.is_active) === 1 || item?.is_active === true;
    },
    productLabel(item) {
      const productName = String(item?.product_name || "").trim();
      if (productName) return productName;
      const productId = parsePositiveId(item?.product_id);
      return productId ? `#${productId}` : "-";
    },
    templateName(item) {
      return String(item?.name || "").trim() || "-";
    },
    formatDateTime(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return normalized;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(parsed.getDate())}.${pad(parsed.getMonth() + 1)}.${parsed.getFullYear()}, ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    },
    formatNumber(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? formatDisplayNumber(parsed) : "0";
    },
    fieldTypeLabel(value) {
      const normalized = String(value || "").trim().toLowerCase();
      const key = `ncrm.contracts.itemFields.types.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    normalizeTableRowPayload(payload) {
      if (!payload) return null;
      if (payload.raw) return payload.raw;
      if (payload.item?.raw) return payload.item.raw;
      if (payload.item) return payload.item;
      return payload;
    },
    onTemplateRowClick(_event, payload) {
      const row = this.normalizeTableRowPayload(payload);
      this.openEditDialog(row);
    },
    openBoard(productId) {
      const normalized = parsePositiveId(productId);
      if (!normalized) return;
      this.$router.push({
        name: "NcrmOnboardingBoard",
        params: { productId: String(normalized) },
      });
    },
    openCreateDialog() {
      this.dialogMode = "create";
      this.editingTemplateId = null;
      this.editingTemplateMeta = null;
      this.createDialog = true;
      this.createForm = buildCreateForm();
      this.lastDefaultOwnerUserId = null;
      this.loadOwners();
      if (!this.products.length) {
        this.loadProducts();
      }
    },
    async openEditDialog(item) {
      const templateId = parsePositiveId(item?.id);
      if (!templateId || this.createLoading) return;

      this.createLoading = true;
      try {
        const { data } = await axios.get(`/ncrm/onboarding/templates/${templateId}`);
        this.dialogMode = "edit";
        this.editingTemplateId = templateId;
        this.editingTemplateMeta = this.normalizeTableRowPayload(item);
        this.mergeTemplateOwnerOptions(data);
        this.createForm = this.mapTemplateDetailsToForm(data);
        this.lastDefaultOwnerUserId = parsePositiveId(this.createForm.default_owner_user_id);
        this.createDialog = true;
        this.loadOwners();
      } catch (error) {
        console.error("[ncrm-onboarding-templates] load details failed", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.onboarding.templates.messages.detailsLoadError"),
          "error"
        );
      } finally {
        this.createLoading = false;
      }
    },
    closeCreateDialog() {
      this.createDialog = false;
      this.dialogMode = "create";
      this.editingTemplateId = null;
      this.editingTemplateMeta = null;
      this.createForm = buildCreateForm();
      this.lastDefaultOwnerUserId = null;
    },
    openDeleteDialog() {
      if (this.dialogMode !== "edit" || !this.editingTemplateId) return;
      const templateName =
        String(this.editingTemplateMeta?.name || this.createForm?.name || "").trim() ||
        `#${this.editingTemplateId}`;
      this.deleteDialog = {
        model: true,
        loading: false,
        confirmText: "",
        hint: this.$t("ncrm.onboarding.templates.deleteDialog.hint", { template: templateName }),
      };
    },
    async confirmDeleteTemplate() {
      if (!this.editingTemplateId) return;
      this.deleteDialog = { ...this.deleteDialog, loading: true };
      try {
        await axios.delete(`/ncrm/onboarding/templates/${this.editingTemplateId}`);
        this.deleteDialog = { model: false, loading: false, confirmText: "", hint: "" };
        this.closeCreateDialog();
        this.showSnackbar(this.$t("ncrm.onboarding.templates.messages.deleted"), "success");
        await this.loadTemplates();
      } catch (error) {
        console.error("[ncrm-onboarding-templates] delete failed", error);
        this.deleteDialog = { ...this.deleteDialog, loading: false };
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.onboarding.templates.messages.deleteError"),
          "error"
        );
      }
    },
    addStage() {
      const nextIndex = this.createForm.stages.length + 1;
      this.createForm.stages.push(buildStage(nextIndex, {
        owner_user_id: parsePositiveId(this.createForm.default_owner_user_id),
      }));
    },
    addGeneralField() {
      const nextIndex = (this.createForm.general_fields || []).length + 1;
      this.createForm.general_fields.push(buildField(nextIndex));
    },
    removeGeneralField(localId) {
      this.createForm.general_fields = (this.createForm.general_fields || [])
        .filter((field) => field.local_id !== localId)
        .map((field, index) => ({ ...field, sort_order: index + 1 }));
    },
    removeStage(localId) {
      if (this.createForm.stages.length <= 1) return;
      this.createForm.stages = this.createForm.stages
        .filter((stage) => stage.local_id !== localId)
        .map((stage, index) => ({ ...stage, sort_order: index + 1 }));
    },
    addTask(stageLocalId) {
      const stage = this.createForm.stages.find((item) => item.local_id === stageLocalId);
      if (!stage) return;
      stage.tasks.push(buildTask(stage.tasks.length + 1));
    },
    addField(stageLocalId) {
      const stage = this.createForm.stages.find((item) => item.local_id === stageLocalId);
      if (!stage) return;
      stage.fields.push(buildField(stage.fields.length + 1));
    },
    removeTask(stageLocalId, taskLocalId) {
      const stage = this.createForm.stages.find((item) => item.local_id === stageLocalId);
      if (!stage) return;
      stage.tasks = stage.tasks
        .filter((task) => task.local_id !== taskLocalId)
        .map((task, index) => ({ ...task, sort_order: index + 1 }));
      if (!stage.tasks.length && !(Array.isArray(stage.fields) && stage.fields.length)) {
        stage.tasks.push(buildTask(1));
      }
    },
    removeField(stageLocalId, fieldLocalId) {
      const stage = this.createForm.stages.find((item) => item.local_id === stageLocalId);
      if (!stage) return;
      stage.fields = (stage.fields || [])
        .filter((field) => field.local_id !== fieldLocalId)
        .map((field, index) => ({ ...field, sort_order: index + 1 }));
      if (!stage.fields.length && !(Array.isArray(stage.tasks) && stage.tasks.length)) {
        stage.tasks.push(buildTask(1));
      }
    },
    mapTemplateDetailsToForm(payload) {
      return mapOnboardingTemplateDetailsToForm(payload);
    },
    buildTemplatePayload({ includeIds = false } = {}) {
      return buildOnboardingTemplatePayload(this.createForm, { includeIds });
    },
    buildCreatePayload() {
      return this.buildTemplatePayload({ includeIds: false });
    },
    buildUpdatePayload() {
      return {
        ...this.buildTemplatePayload({ includeIds: true }),
        apply_to_existing_onboardings: true,
      };
    },
    async loadTemplates() {
      this.loading = true;
      try {
        const { data } = await axios.get("/ncrm/onboarding/templates", {
          params: { include_inactive: this.includeInactive ? 1 : 0 },
        });
        this.templates = Array.isArray(data?.items) ? data.items : [];
        this.mergeOwnerOptions(this.templates);
      } catch (error) {
        console.error("[ncrm-onboarding-templates] load failed", error);
        this.templates = [];
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.onboarding.templates.messages.loadError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    async loadProducts() {
      this.productLoading = true;
      try {
        this.products = await fetchOnboardingTemplateProducts(async (params) => {
          const response = await axios.get("/ncrm/products", { params });
          return response?.data || null;
        });
      } catch (error) {
        console.error("[ncrm-onboarding-templates] load products failed", error);
        this.products = [];
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.onboarding.templates.messages.productsLoadError"),
          "error"
        );
      } finally {
        this.productLoading = false;
      }
    },
    async submitTemplate() {
      if (!this.canSubmit) return;
      this.createLoading = true;
      try {
        if (this.dialogMode === "edit" && this.editingTemplateId) {
          const { data } = await axios.patch(
            `/ncrm/onboarding/templates/${this.editingTemplateId}`,
            this.buildUpdatePayload()
          );
          const generatedTaskCount = Number(data?.propagation?.generated_task_count || 0);
          this.showSnackbar(
            this.$t("ncrm.onboarding.templates.messages.updated", { count: generatedTaskCount }),
            "success"
          );
        } else {
          await axios.post("/ncrm/onboarding/templates", this.buildCreatePayload());
          this.showSnackbar(this.$t("ncrm.onboarding.templates.messages.created"), "success");
        }
        this.closeCreateDialog();
        await this.loadTemplates();
      } catch (error) {
        console.error("[ncrm-onboarding-templates] submit failed", error);
        const messageKey =
          this.dialogMode === "edit"
            ? "ncrm.onboarding.templates.messages.updateError"
            : "ncrm.onboarding.templates.messages.createError";
        this.showSnackbar(error?.response?.data?.error || this.$t(messageKey), "error");
      } finally {
        this.createLoading = false;
      }
    },
  },
};
</script>

<style src="./NcrmOnboardingTemplates.styles.css" />
